import React, { useMemo } from 'react';

import { AutocompleteDropdown, MultiSelectDropdown, SingleSelectDropdown } from 'pages/common/components/Autocomplete/Autocomplete';

import { Checkbox } from './checkbox-styled-component';
import { ErrorMessage, FormInput, FormTextarea, InputWrapper } from './input-styled-components';
import { ToggleSelect } from './ToggleSelect/ToggleSelect';

export enum InputType {
  TEXT = 'text',
  PASSWORD = 'password',
  EMAIL = 'email',
  TEL = 'tel',
  NUMBER = 'number',
  TEXTAREA = 'textarea',
  CHECKBOX = 'checkbox',
  TOGGLE = 'toggle',

  // Autocomplete variants
  AUTOCOMPLETE_DROPDOWN = 'autocomplete',
  SINGLE_SELECT_DROPDOWN = 'single-select',
  MULTI_SELECT_DROPDOWN = 'multi-select',
}

const genericInputTypes: InputType[] = [
  InputType.TEXT,
  InputType.PASSWORD,
  InputType.TEL,
  InputType.EMAIL,
  InputType.NUMBER,
];

const specialInputTypes: Partial<Record<InputType, React.FunctionComponent>> = {
  [InputType.TEXTAREA]: FormTextarea,
  [InputType.CHECKBOX]: Checkbox,

  // The inferred type of these components is `React.JSX.Element`, so we need to cast them to `React.FunctionComponent`
  [InputType.TOGGLE]: ToggleSelect as React.FunctionComponent,
  [InputType.AUTOCOMPLETE_DROPDOWN]: AutocompleteDropdown as React.FunctionComponent,
  [InputType.SINGLE_SELECT_DROPDOWN]: SingleSelectDropdown as React.FunctionComponent,
  [InputType.MULTI_SELECT_DROPDOWN]: MultiSelectDropdown as React.FunctionComponent,
};

const getComponentForInputType = (inputType: InputType): React.FunctionComponent<any> => {
  const isGenericInput = genericInputTypes.includes(inputType);

  if (isGenericInput) {
    return FormInput;
  }

  const SpecialInput = specialInputTypes[inputType]!;

  if (!SpecialInput) {
    throw new Error(`Input type ${inputType} is not supported`);
  }

  return SpecialInput;
};

const reactComponentInputs = [
  InputType.TOGGLE,
];

interface InputProps {
  type: InputType;
  errorMessage?: string;
  isSensitive?: boolean;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

export const Input = ({
  type,
  errorMessage,
  isSensitive = false,
  className,
  ...props
}: InputProps) => {
  const InputComponent = useMemo(() => getComponentForInputType(type), [type]);

  // styled-components use $transient props, but we also have React components, so we need to handle both cases
  const propsToPass = useMemo(() => {
    const isInvalid = Boolean(errorMessage);
    const inputIsSensitive = isSensitive || type === InputType.PASSWORD;

    const classNameToPass = (() => {
      if (!inputIsSensitive) {
        return className;
      }

      if (!className) {
        return 'sensitive-data';
      }

      return `${className} sensitive-data`;
    })();

    if (reactComponentInputs.includes(type)) {
      return {
        ...props,
        className: classNameToPass,
        isSensitive: inputIsSensitive,
        isInvalid,
      };
    }

    return { ...props, className: classNameToPass, $isInvalid: isInvalid };
  }, [props, errorMessage, type, isSensitive, className]);

  return (
    <InputWrapper>
      <InputComponent type={type} {...propsToPass} />
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputWrapper>
  );
};
